import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Booker } from '@calcom/atoms';
import { captureException } from '@sentry/remix';
import { MobileModalElement, ModalElement } from '@shared/dream-components/src';

import useIsMobile from '~/hooks/useIsMobile';
import type { CalComBooking } from '~/interfaces/cal_com';
import type { CalComFulfillmentMetadata } from '~/interfaces/fulfillment';
import { CAL_COM_API_URL } from '~/utils/cal_com/constants';

// Cal.com API version header used by @calcom/atoms internally
const CAL_API_VERSION = '2024-06-14';

interface Props {
  eventSlug: string;
  calComUserName: string;
  calComClientId: string;
  currentSubscriberName?: string;
  currentSubscriberEmail?: string;
  onClose: () => void;
  onBookingSuccess: (bookingData: CalComBooking) => void;
  isOpen: boolean;
  shouldDisableConfirmButton?: boolean;
  fulfillmentMetadata?: CalComFulfillmentMetadata;
  selectedDate?: string;
}

export const BookCoachingSessionModal = ({
  fulfillmentMetadata,
  eventSlug,
  calComUserName,
  calComClientId,
  currentSubscriberName,
  currentSubscriberEmail,
  onClose,
  onBookingSuccess,
  isOpen,
  shouldDisableConfirmButton = false,
  selectedDate,
}: Props) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isLoadingCalComAtoms, setIsLoadingCalComAtoms] = useState(false);
  const [hasLoadedCalComAtoms, setHasLoadedCalComAtoms] = useState(false);

  // Load Cal.com styles only once
  useEffect(() => {
    if (!isOpen || hasLoadedCalComAtoms) return;
    setIsLoadingCalComAtoms(true);

    const loadCalComStyles = async () => {
      try {
        await import('@calcom/atoms/globals.min.css');
        setHasLoadedCalComAtoms(true);
      } catch (error) {
        captureException(error);
        toast.error(t('products.error.loading_cal_com_styles'));
        onClose();
      } finally {
        setIsLoadingCalComAtoms(false);
      }
    };

    loadCalComStyles();
  }, [isOpen, hasLoadedCalComAtoms]);

  // Bypass the Booker's internal booking mutation to avoid calcom/cal.com#28050:
  // The Booker's onSuccess handler crashes for certain email domains (e.g. @example.com)
  // because Cal.com returns responses: null, causing a null dereference.
  // Using handleCreateBooking, we make the API call ourselves and skip the buggy handler entirely.
  const handleCreateBooking = useCallback(
    async (input: Record<string, unknown>) => {
      try {
        const response = await fetch(`${CAL_COM_API_URL}/bookings`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-cal-client-id': calComClientId,
            'cal-api-version': CAL_API_VERSION,
          },
          body: JSON.stringify(input),
        });

        if (!response.ok) {
          if (response.status === 409) {
            toast.error(t('products.error.booking_already_exists'));
            return;
          }
          throw new Error(`Booking failed: ${response.status}`);
        }

        const json = await response.json();
        if (json?.status === 'success' && json?.data?.uid) {
          const responses = input.responses as { email?: string; name?: string } | undefined;
          const attendeeEmail = responses?.email ?? '';
          const attendeeName = responses?.name ?? '';

          const bookingData: CalComBooking = {
            uid: json.data.uid,
            attendees: [{ email: attendeeEmail, name: attendeeName }],
            bookingFieldsResponses: json.data.responses ?? {
              email: attendeeEmail,
              guests: [],
              location: { value: '', optionValue: '' },
              name: attendeeName,
              notes: '',
            },
          };
          onBookingSuccess(bookingData);
        }
      } catch (error) {
        captureException(error instanceof Error ? error : new Error(String(error)));
      }
    },
    [onBookingSuccess, calComClientId, t],
  );

  const handleBookingError = useCallback(
    (error: unknown) => {
      const status =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { status?: number } }).response?.status
          : undefined;

      if (status === 409) {
        toast.error(t('products.error.booking_already_exists'));
      } else {
        captureException(error instanceof Error ? error : new Error(String(error)));
      }
    },
    [t],
  );

  const defaultFormValues = useMemo(() => {
    const values: Record<string, string | string[]> = {};

    if (fulfillmentMetadata?.cal_booking_fields_responses) {
      values.name = fulfillmentMetadata.cal_booking_fields_responses.name ?? '';
      values.email = fulfillmentMetadata.cal_booking_fields_responses.email;
    } else if (currentSubscriberEmail) {
      values.name = currentSubscriberName ?? '';
      values.email = currentSubscriberEmail;
    }

    return Object.keys(values).length ? values : undefined;
  }, [fulfillmentMetadata, currentSubscriberName, currentSubscriberEmail]);

  if (!isOpen || isLoadingCalComAtoms || !hasLoadedCalComAtoms) {
    return null;
  }

  const bookerElement = (
    <Booker
      rescheduleUid={fulfillmentMetadata?.cal_booking_uid}
      eventSlug={eventSlug}
      username={calComUserName}
      handleCreateBooking={handleCreateBooking}
      onCreateBookingError={handleBookingError}
      defaultFormValues={defaultFormValues}
      selectedDate={selectedDate ? new Date(selectedDate) : undefined}
      hideBranding
      confirmButtonDisabled={shouldDisableConfirmButton}
    />
  );

  if (isMobile) {
    return createPortal(
      <MobileModalElement
        isOpen={isOpen}
        onOpenChange={onClose}
        isLoading={shouldDisableConfirmButton}
        showCloseButton={!shouldDisableConfirmButton}
        hideOverflow={false}
        element={{
          type: 'mobileModal',
          content: [],
        }}
      >
        {bookerElement}
      </MobileModalElement>,
      document.body,
    );
  }

  return createPortal(
    <ModalElement
      isOpen={isOpen}
      onOpenChange={onClose}
      isLoading={shouldDisableConfirmButton}
      showCloseButton={false}
      hideOverflow={false}
      element={{
        type: 'modal',
        content: [],
      }}
    >
      {bookerElement}
    </ModalElement>,
    document.body,
  );
};
